import { FC, useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { useOrientationChange } from "hooks";
import styles from "./popover.module.sass";

type Placement = "top" | "bottom" | "left" | "right";
type TriggerType = "click" | "hover";
type ScrollBehavior = "close";

type Props = {
    trigger: React.ReactNode;
    children: React.ReactNode;
    placement?: Placement;
    triggerType?: TriggerType;
    triggerClassName?: string;
    disabled?: boolean;
    offsetTop?: number;
    offsetLeft?: number;
    arrowOffset?: number;
    onOpen?: (event?: MouseEvent) => void;
    onClose?: (event?: MouseEvent) => void;
    onPositionChange?: (position: { top: number; left: number }) => void;
    onOutsideClick?: (event?: MouseEvent) => void;
    className?: string;
    contentClassName?: string;
    showArrow?: boolean;
    autoClose?: boolean;
    closeOnOrientationChange?: boolean;
    maxHeight?: string;
    scrollRef?: React.RefObject<Element | null>;
    scrollBehavior?: ScrollBehavior;
    initialPosition?: { top: number | null; left: number | null };
};

const Popover: FC<Props> = ({
    trigger,
    children,
    placement = "right",
    disabled = false,
    triggerType = "click",
    offsetTop = 16,
    arrowOffset = 0,
    onOpen,
    onClose,
    onPositionChange,
    onOutsideClick,
    className,
    contentClassName,
    autoClose = true,
    closeOnOrientationChange = false,
    maxHeight = "60vh",
    scrollRef,
    scrollBehavior,
    initialPosition,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [animating, setAnimating] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver>(undefined);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();

        let top = 0;
        let left = 0;

        const width = triggerRef.current.offsetWidth;
        const height = triggerRef.current.offsetHeight;

        switch (placement) {
            case "top":
                top = triggerRect.top;
                break;
            case "bottom":
                top = triggerRect.bottom + offsetTop;
                left = triggerRect.right;
                break;
            case "left":
                top = triggerRect.top;
                break;
            case "right":
                top = triggerRect.top;
                left = triggerRect.right;
                break;
        }

        setPosition({ top, left, width, height });
        onPositionChange?.({ top, left });
    }, [placement, offsetTop, onPositionChange]);

    const setTriggerActive = useCallback((isActive: boolean) => {
        if (triggerRef.current) {
            isActive
                ? triggerRef.current.classList.add("popover-trigger-active")
                : triggerRef.current.classList.remove("popover-trigger-active");
        }
    }, []);

    const handleTriggerClick = useCallback(() => {
        if (triggerType === "click") {
            const value = !isOpen;
            value ? onOpen?.() : onClose?.();
            setIsOpen(value);
            setTriggerActive(value);
            if (value) {
                if (initialPosition?.top && initialPosition?.left) {
                    setAnimating(true);
                    setPosition((prev) => ({
                        ...prev,
                        top: initialPosition.top as number,
                        left: initialPosition.left as number,
                    }));

                    setTimeout(() => {
                        calculatePosition();
                        setAnimating(false);
                    }, 100);
                } else {
                    calculatePosition();
                }
            }
        }
    }, [
        triggerType,
        isOpen,
        calculatePosition,
        onOpen,
        onClose,
        setIsOpen,
        setTriggerActive,
        initialPosition?.top,
        initialPosition?.left,
    ]);

    const handleTriggerMouseEnter = useCallback(() => {
        if (triggerType === "hover") {
            onOpen?.();
            setIsOpen(true);
            setTriggerActive(true);
            calculatePosition();
        }
    }, [triggerType, calculatePosition, onOpen, setIsOpen, setTriggerActive]);

    const handleTriggerMouseLeave = useCallback(() => {
        if (triggerType === "hover") {
            onClose?.();
            setIsOpen(false);
            setTriggerActive(false);
        }
    }, [triggerType, onClose, setIsOpen, setTriggerActive]);

    useEffect(() => {
        if (!autoClose) return;

        const handleOutsideClick = (event: MouseEvent) => {
            if (
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node) &&
                contentRef.current &&
                !contentRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setTriggerActive(false);
                onClose?.();
                onOutsideClick?.(event);
            }
        };

        document.addEventListener("click", handleOutsideClick);
        return () => document.removeEventListener("click", handleOutsideClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (scrollBehavior !== "close") return;

        const handler = () => {
            setIsOpen(false);
            setTriggerActive(false);
            onClose?.();
        };

        const scrollRefCurrent = scrollRef?.current;

        if (scrollRefCurrent) {
            scrollRefCurrent.addEventListener("scroll", handler);
            return () => scrollRefCurrent?.removeEventListener("scroll", handler);
        } else {
            window.addEventListener("scroll", handler);
            return () => window.removeEventListener("scroll", handler);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOrientationChange = useCallback(() => {
        setIsOpen(false);
        setTriggerActive(false);
        onClose?.();
    }, [onClose, setIsOpen, setTriggerActive]);

    useOrientationChange(handleOrientationChange, closeOnOrientationChange);

    useEffect(() => {
        if (!isOpen || !contentRef.current) return;

        const onResize = (entries: any) => {
            if (placement === "right" || placement === "left") {
                if (entries[0].contentRect.height >= window.innerHeight - position.top) {
                    if (contentRef.current) {
                        contentRef.current.style.top = `${
                            window.innerHeight - 17 - entries[0].contentRect.height
                        }px`;
                    }
                } else {
                    if (contentRef.current) {
                        contentRef.current.style.top = `${position.top}px`;
                    }
                }
            }

            if (placement === "bottom") {
                if (contentRef.current) {
                    const contentWidth = entries[0].contentRect.width;
                    const viewportWidth = window.innerWidth;
                    const triggerLeft = position.left;
                    const triggerWidth = position.width;

                    const setContentParentPosition = (left: number, arrowLeft: number) => {
                        if (contentRef.current?.parentElement) {
                            contentRef.current.parentElement.style.left = `${left}px`;
                            contentRef.current.parentElement.style.setProperty(
                                "--trigger-arrow-left",
                                `${arrowLeft}px`,
                            );
                        }
                    };

                    // Check if content would overflow on the right
                    if (triggerLeft + contentWidth > viewportWidth) {
                        if (triggerLeft > viewportWidth) {
                            // Adjust left position to prevent content overflow and viewpoert overflow
                            contentRef.current.style.left = `${viewportWidth - contentWidth - 16}px`;
                            setContentParentPosition(viewportWidth - 16, -16 - arrowOffset);
                        } else {
                            // Adjust left position to prevent content overflow
                            contentRef.current.style.left = `${triggerLeft - contentWidth}px`;
                            setContentParentPosition(
                                triggerLeft,
                                -(triggerWidth / 2) - arrowOffset,
                            );
                        }
                    } else {
                        // Check if content would overflow on the left scroll container
                        if (
                            scrollRef?.current &&
                            scrollRef?.current?.getBoundingClientRect().left >
                                triggerLeft - triggerWidth
                        ) {
                            const containerLeft = scrollRef?.current?.getBoundingClientRect().left;

                            contentRef.current.style.left = `${containerLeft}px`;
                            setContentParentPosition(containerLeft, 16);
                        } else {
                            // Default left position to trigger left
                            contentRef.current.style.left = `${triggerLeft - triggerWidth}px`;
                            setContentParentPosition(
                                triggerLeft - triggerWidth,
                                triggerWidth / 2 + arrowOffset,
                            );
                        }
                    }
                }
            }
        };

        const currentRef = contentRef.current;

        resizeObserverRef.current = new ResizeObserver((entries) => {
            // This code suppose to fix following error: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
            window.requestAnimationFrame(() => {
                if (!Array.isArray(entries) || !entries.length) {
                    return;
                }
                onResize(entries);
            });
        });
        resizeObserverRef.current?.observe(currentRef);

        return () => {
            if (currentRef) {
                resizeObserverRef.current?.unobserve(currentRef);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, position, arrowOffset, placement]);

    if (disabled) return <>{trigger}</>;

    const renderContent = () => {
        const content = (
            <div
                className={classNames(styles.popover, styles[placement], className, {
                    [styles.notVisible]: animating,
                    [styles.visible]: !animating,
                })}
                style={
                    {
                        "--trigger-top": `${position.top}px`,
                        "--trigger-left": `${position.left}px`,
                        "--trigger-height": `${position.height}px`,
                        "--trigger-width": `${position.width}px`,
                        "--trigger-arrow-offset": `${arrowOffset}px`,
                    } as React.CSSProperties & { [key: string]: string }
                }>
                <div
                    className={classNames(styles.popoverContent, contentClassName)}
                    ref={contentRef}
                    style={{ maxHeight }}>
                    {children}
                </div>
                <span className={styles.backdrop} />
            </div>
        );

        return createPortal(content, document.body);
    };

    return (
        <>
            <div
                ref={triggerRef}
                onClick={handleTriggerClick}
                onMouseEnter={handleTriggerMouseEnter}
                onMouseLeave={handleTriggerMouseLeave}
                className={classNames(styles.trigger, "popover-trigger-container")}>
                {trigger}
            </div>
            {isOpen && renderContent()}
        </>
    );
};

export default Popover;
